/**
 * common/abbr.js
 *
 * Sense attribute taxonomy (ported from TMS). Maps the short codes used in the
 * analyst event string (e.g. "SP", "LS") to a human attribute group (`type`),
 * a display name, and the per-event-id labels.
 *
 * Trimmed to the pieces the Analyst Portal needs (attribTitle, specialEventsMap,
 * topEventCategories) — the rating/category builders in the TMS source depend on
 * config that doesn't exist here and are omitted.
 */

export const attribTitle = {
  SP: { id: "SP", name: "Short Pass", type: "passing", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass", 3: "Assist" } },
  LP: { id: "LP", name: "Long Pass", type: "passing", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass", 3: "Assist" } },
  TB: { id: "TB", name: "Through Ball", type: "passing", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass", 3: "Assist" } },
  C: { id: "C", name: "Cross", type: "passing", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass", 3: "Assist" } },
  LS: { id: "LS", name: "Long Shot", type: "shooting", events: { 0: "Off Target", 1: "Simple Shot", 2: "Hit Goal Post", 3: "Brilliant Shot", 4: "Goal" } },
  CS: { id: "CS", name: "Close Shot", type: "shooting", events: { 0: "Off Target", 1: "Simple Shot", 2: "Hit Goal Post", 3: "Brilliant Shot", 4: "Goal" } },
  DC: { id: "DC", name: "Difficult Recieves", type: "dribbling", events: { 0: "Unsuccessful", 1: "Successful" } },
  T: { id: "T", name: "Touches", type: "ball_control", events: { 0: "Unsuccessful", 1: "Simple Touch", 2: "Difficult Receive" } },
  DR: { id: "DR", name: "Dribbling", type: "dribbling", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  PC: { id: "PC", name: "Ball Carry", type: "pace", events: { 1: "Ball Carry Short", 2: "Ball Carry Medium", 3: "Ball Carry Long" } },
  AD: { id: "AD", name: "Aerial Duels", type: "physical", events: { 0: "Duel Lost", 1: "Duel Won" } },
  GD: { id: "GD", name: "Ground Duels", type: "physical", events: { 0: "Duel Lost", 1: "Duel Won" } },
  H: { id: "H", name: "Headers Won", type: "physical", events: { 0: "Off Target", 1: "Simple Header", 2: "Hit Goal Post", 3: "Brilliant Header", 4: "Goal" } },
  IN: { id: "IN", name: "Interceptions", type: "defending", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  CL: { id: "CL", name: "Clearances", type: "defending", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial", 3: "Goal Line Clearance" } },
  ST: { id: "ST", name: "Standing Tackles", type: "defending", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  SL: { id: "SL", name: "Sliding Tackles", type: "defending", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  PR: { id: "PR", name: "Pressing", type: "defending", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  GS: { id: "GS", name: "Saves", type: "goalkeeping", events: { 0: "Goal Conceded", 1: "Simple Shot Saved", 2: "Key Shot Saved", 3: "Brilliant Shot Saved" } },
  GH: { id: "GH", name: "Handling", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Loose Ball Handled", 2: "Key Ball Handled" } },
  GT: { id: "GT", name: "Goalkeeper Throw", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Simple Throw", 2: "Key Throw" } },
  HB: { id: "HB", name: "Handball", type: "special_actions", events: { 1: "Handball" } },
  OFF: { id: "OFF", name: "Offside", type: "special_actions", events: { 1: "Offside" } },
  THW: { id: "THW", name: "Throw", type: "special_actions", events: { 0: "Unsuccessful", 1: "Simple Throw", 2: "Key Throw", 3: "Assist Throw" } },
  XSP: { id: "XSP", name: "Short Pass Receive Attempts", type: "x_passing", events: { 0: "Received Unsuccessful", 1: "Received Simple Pass", 2: "Received Key Pass", 3: "Received Assist" } },
  XLP: { id: "XLP", type: "x_passing", name: "Long Pass Receive Attempts", events: { 0: "Received Unsuccessful", 1: "Received Simple Pass", 2: "Received Key Pass", 3: "Received Assist" } },
  XTB: { id: "XTB", type: "x_passing", name: "Through Ball Receive Attempts", events: { 0: "Received Unsuccessful", 1: "Received Simple Pass", 2: "Received Key Pass", 3: "Received Assist" } },
  XC: { id: "XC", type: "x_passing", name: "Cross Receive Attempts", events: { 0: "Received Unsuccessful", 1: "Received Simple Pass", 2: "Received Key Pass", 3: "Received Assist" } },
  XST: { id: "XST", type: "x_defending", name: "Standing Tackle Encountered", events: { 0: "Tackle Evaded", 1: "Tackled-Simple", 2: "Tackled-Crucial" } },
  XSL: { id: "XSL", type: "x_defending", name: "Sliding Tackle Encountered", events: { 0: "Tackle Evaded", 1: "Tackled-Simple", 2: "Tackled-Crucial" } },
  XIN: { id: "XIN", type: "x_defending", name: "Interception Encountered", events: { 0: "Interception Evaded", 1: "Intercepted-Simple", 2: "Intercepted-Crucial" } },
  XPR: { id: "XPR", type: "x_defending", name: "Pressure Encountered", events: { 0: "Pressed Evaded", 1: "Pressed-Simple", 2: "Pressed-Crucial" } },
  XAD: { id: "XAD", name: "Aerial Duels", type: "x_physical", events: { 0: "Duel Won", 1: "Duel Lost" } },
  XGD: { id: "XGD", type: "x_physical", name: "Ground Duels", events: { 0: "Duel Won", 1: "Duel Lost" } },
  XDR: { id: "XDR", type: "x_dribbling", name: "Dribbling Encountered", events: { 0: "Dribble Resisted", 1: "Dribbled Past-Simple", 2: "Dribbled Past-Crucial" } },
  XGT: { id: "XGT", type: "x_goalkeeping", name: "Goalkeeper Throw Receive Attempts", events: { 0: "Received Unsuccessful", 1: "Received Simple Throw", 2: "Received Key Throw" } },
  XTHW: { id: "XTHW", type: "x_special_actions", name: "Throw In Receive Attempts", events: { 0: "Received Unsuccessful", 1: "Received Simple Throw", 2: "Received Key Throw", 3: "Received Assist Throw" } },
  DL: { id: "DL", name: "Delay", type: "special_actions", events: { 1: "Delay" } },
  GPBOT: { id: "GPBOT", name: "Goalkeeper pass beyond own third", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass" } },
  GPIOT: { id: "GPIOT", name: "Goalkeeper pass inside own third", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Simple Pass", 2: "Key Pass" } },
  CC: { id: "CC", name: "Crosses Collected", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Successful", 2: "Crucial" } },
  CSS: { id: "CSS", name: "Close shot saved", type: "goalkeeping", events: { 0: "Goal Conceded", 1: "Simple Shot Saved", 2: "Key Shot Saved", 3: "Brilliant Shot Saved" } },
  LSS: { id: "LSS", name: "Long shot saved", type: "goalkeeping", events: { 0: "Goal Conceded", 1: "Simple Shot Saved", 2: "Key Shot Saved", 3: "Brilliant Shot Saved" } },
  HS: { id: "HS", name: "Header saved", type: "goalkeeping", events: { 0: "Goal Conceded", 1: "Simple Shot Saved", 2: "Key Shot Saved", 3: "Brilliant Shot Saved" } },
  GHPA: { id: "GHPA", name: "Goalkeeping Handle Previous Action", type: "goalkeeping", events: { 0: "Unsuccessful", 1: "Simple Handle", 2: "Key Handle", 3: "Crucial Handle" } },
  GSPA: { id: "GSPA", name: "Goalkeeping Saves Previous Action", type: "goalkeeping", events: { 0: "Goal Conceded", 1: "Simple Save", 2: "Key Save", 3: "Brilliant Save" } },
};

export const specialEventsMap = {
  G: "Goal",
  CN: "Corner",
  OF: "Offside",
  F: "Foul",
  OG: "Own Goal",
  YC: "Yellow Card",
  RC: "Red Card",
  PK: "Penalty",
  FK: "Free Kick",
  HB: "Hand Ball",
  GK: "Goal Kick",
  X: "None",
};

export const topEventCategories = {
  "Goal Scored": ["Goal"],
  "Chances Created": ["Key Pass", "Assist"],
  "Goal Conceded": ["Goal"],
  "Chances Conceded": ["Key Pass", "Assist"],
  "Set Pieces": ["Corner", "Free Kick", "Penalty", "Goal Kick"],
  Fouls: ["Foul", "Hand Ball", "Red Card", "Yellow Card", "Offside"],
};

/** Friendly label for an attribute `type` (e.g. "x_passing" → "Passing (Received)"). */
export const ATTRIBUTE_LABEL = {
  passing: "Passing",
  shooting: "Shooting",
  dribbling: "Dribbling",
  ball_control: "Ball Control",
  pace: "Pace",
  physical: "Physical",
  defending: "Defending",
  goalkeeping: "Goalkeeping",
  special_actions: "Special Actions",
  x_passing: "Passing (Received)",
  x_defending: "Defending (Faced)",
  x_physical: "Physical (Faced)",
  x_dribbling: "Dribbling (Faced)",
  x_goalkeeping: "Goalkeeping (Received)",
  x_special_actions: "Special Actions (Received)",
};
